import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Trash2, Edit2, Upload, Download, ChevronDown, ChevronUp, Loader2, Search, CheckCircle, Info } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

// ── Date helpers ──────────────────────────────────────────────────────────────
function parseAttestrDate(raw) {
  if (!raw) return '';
  const dmy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const dmy2 = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy2) return `${dmy2[3]}-${dmy2[2]}-${dmy2[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return raw;
}

// Attestr uses "1800-01-01" to indicate "not available / not applicable"
function validAttestrDate(raw) {
  const d = parseAttestrDate(raw);
  if (!d) return '';
  // Reject clearly invalid sentinel years
  const year = parseInt(d.substring(0, 4), 10);
  if (year < 1950 || year > 2100) return '';
  return d;
}

// ---- Config per record type ----
const RECORD_CONFIG = {
  PUC:         { label: 'PUC Certificate',      fields: ['reference_no','issue_date','expiry_date'],                                          refLabel: 'Certificate No' },
  INSURANCE:   { label: 'Insurance',             fields: ['reference_no','provider','issue_date','expiry_date','amount'],                      refLabel: 'Policy No',     providerLabel: 'Insurer' },
  RC_BOOK:     { label: 'RC Book',               fields: ['reference_no','provider','issue_date','expiry_date'],                               refLabel: 'RC Number',     providerLabel: 'Issue State' },
  FITNESS:     { label: 'Fitness Certificate',   fields: ['reference_no','provider','issue_date','expiry_date'],                               refLabel: 'Certificate No',providerLabel: 'Issued State' },
  PERMIT:      { label: 'Permit Record',         fields: ['reference_no','provider','issue_date','expiry_date'],                               refLabel: 'Permit No',     providerLabel: 'Permit Type' },
  SERVICE:     { label: 'Service Record',        fields: ['reference_no','issue_date','provider','amount','remarks'],                          refLabel: 'Kilometer (KM)', providerLabel: 'Service Center' },
  TYRE_CHANGE: { label: 'Tyre Change',           fields: ['reference_no','issue_date','provider','details','amount','remarks'],                refLabel: 'Kilometer (KM)', providerLabel: 'Which Tyre Changed',
    multiOptions: ['RIGHT FRONT TYRE','RIGHT REAR TYRE 1','RIGHT REAR TYRE 2','RIGHT REAR TYRE 3','RIGHT REAR TYRE 4','LEFT FRONT TYRE','LEFT REAR TYRE 1','LEFT REAR TYRE 2','LEFT REAR TYRE 3','LEFT REAR TYRE 4'] },
  BATTERY_CHANGE: { label: 'Battery Change',     fields: ['reference_no','issue_date','provider','expiry_date','details','amount','remarks'],  refLabel: 'Kilometer (KM)', providerLabel: 'Battery For', expiryLabel: 'Battery Warranty Upto' },
  UREA:        { label: 'Urea Record',           fields: ['reference_no','issue_date','amount','remarks'],                                     refLabel: 'Kilometer (KM)' },
  GENERAL:     { label: 'General Maintenance',   fields: ['reference_no','issue_date','provider','amount','remarks'],                          refLabel: 'Kilometer (KM)', providerLabel: 'Maintenance Type' },
};

const DOCUMENT_TYPES = ['PUC','INSURANCE','RC_BOOK','FITNESS','PERMIT'];
const MAINTENANCE_TYPES = ['SERVICE','TYRE_CHANGE','BATTERY_CHANGE','UREA','GENERAL'];

const emptyForm = () => ({
  reference_no: '', provider: '', issue_date: '', expiry_date: '',
  amount: '', details: '', remarks: ''
});

// ─────────────────────────────────────────────────────────────────────────────
// RecordSection — individual collapsible section for one record type
// ─────────────────────────────────────────────────────────────────────────────
const RecordSection = ({ vehicleId, recordType, driveConnected, backendUrl, getAuthHeaders, prefillData }) => {
  const { toast } = useToast();
  const cfg = RECORD_CONFIG[recordType];
  // ← FIX: hasField was previously missing, causing "ReferenceError: hasField is not defined"
  const hasField = (f) => cfg.fields?.includes(f);

  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  // Apply Attestr prefill when RC data is provided by parent
  useEffect(() => {
    if (prefillData && Object.keys(prefillData).some(k => prefillData[k])) {
      setForm({ ...emptyForm(), ...prefillData });
      setEditRecord(null);
      setShowForm(true);
      setOpen(true);
    }
  }, [prefillData]);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/vendor-vehicles/${vehicleId}/records?record_type=${recordType}`, { headers: getAuthHeaders() });
      if (res.ok) setRecords(await res.json());
    } catch { /* silent */ }
  }, [vehicleId, recordType, backendUrl, getAuthHeaders]);

  useEffect(() => { if (open) fetchRecords(); }, [open, fetchRecords]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        record_type: recordType,
        reference_no: form.reference_no || null,
        provider: form.provider || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
        amount: form.amount ? parseFloat(form.amount) : null,
        details: form.details || null,
        remarks: form.remarks || null,
      };
      const url = editRecord
        ? `${backendUrl}/api/vendor-vehicles/${vehicleId}/records/${editRecord.id}`
        : `${backendUrl}/api/vendor-vehicles/${vehicleId}/records`;
      const res = await fetch(url, { method: editRecord ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to save'); }
      toast({ title: "Success", description: editRecord ? "Record updated" : "Record added" });
      setShowForm(false); setEditRecord(null); setForm(emptyForm());
      fetchRecords();
    } catch (e) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleEdit = (record) => {
    setEditRecord(record);
    setForm({
      reference_no: record.reference_no || '', provider: record.provider || '',
      issue_date: record.issue_date || '', expiry_date: record.expiry_date || '',
      amount: record.amount || '', details: record.details || '', remarks: record.remarks || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await fetch(`${backendUrl}/api/vendor-vehicles/${vehicleId}/records/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      toast({ title: "Deleted" });
      fetchRecords();
    } catch { toast({ title: "Error", description: "Failed to delete", variant: "destructive" }); }
  };

  const handleUpload = async (recordId, file) => {
    if (!file) return;
    try {
      setUploadingId(recordId);
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${backendUrl}/api/vendor-vehicles/${vehicleId}/records/${recordId}/attachment`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }, body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      toast({ title: "Uploaded" });
      fetchRecords();
    } catch { toast({ title: "Error", description: "Upload failed", variant: "destructive" }); }
    finally { setUploadingId(null); }
  };

  const handleDownload = (record) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${backendUrl}/api/drive/files/${record.drive_file_id}/download?token=${token}`, '_blank');
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        onClick={() => setOpen(p => !p)}
        data-testid={`section-${recordType}`}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-text-dark font-ibm text-sm">{cfg.label}</span>
          {records.length > 0 && (
            <span className="bg-accent-blue text-white text-xs font-bold px-2 py-0.5 rounded-full">{records.length}</span>
          )}
          {prefillData && Object.keys(prefillData).some(k => prefillData[k]) && (
            <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full border border-green-200">RC auto-filled</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />}
      </button>

      {open && (
        <div className="p-5 space-y-4">
          {/* Inline add/edit form */}
          {showForm && (
            <div className="bg-blue-50 border border-accent-blue/20 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-text-dark font-ibm">{editRecord ? 'Edit Record' : 'Add Record'}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {hasField('reference_no') && cfg.refLabel && (
                  <div>
                    <Label className="text-xs font-ibm text-text-dark">{cfg.refLabel}</Label>
                    <Input data-testid={`${recordType}-ref-no`} value={form.reference_no} onChange={e => setForm(p => ({...p, reference_no: e.target.value}))} className="mt-1 h-8 text-sm font-ibm" />
                  </div>
                )}
                {hasField('provider') && cfg.multiOptions ? (
                  <div className="col-span-2 md:col-span-3">
                    <Label className="text-xs font-ibm text-text-dark">{cfg.providerLabel || 'Provider'}</Label>
                    <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 bg-white border border-input rounded-md p-2">
                      {cfg.multiOptions.map(opt => {
                        const selected = (form.provider || '').split(',').map(s => s.trim()).filter(Boolean);
                        const checked = selected.includes(opt);
                        const toggle = () => {
                          const next = checked ? selected.filter(s => s !== opt) : [...selected, opt];
                          setForm(p => ({ ...p, provider: next.join(', ') }));
                        };
                        return (
                          <label key={opt} className={`flex items-center gap-1.5 text-xs font-ibm cursor-pointer px-2 py-1 rounded border ${checked ? 'bg-accent-blue/10 border-accent-blue text-accent-blue font-semibold' : 'border-gray-200 text-text-secondary hover:border-accent-blue/50'}`}>
                            <input type="checkbox" className="hidden" checked={checked} onChange={toggle} />
                            <span className={`w-3.5 h-3.5 border flex-shrink-0 flex items-center justify-center rounded-sm ${checked ? 'bg-accent-blue border-accent-blue' : 'border-gray-400'}`}>
                              {checked && <span className="text-white text-[9px] font-bold">✓</span>}
                            </span>
                            {opt}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : hasField('provider') && (
                  <div>
                    <Label className="text-xs font-ibm text-text-dark">{cfg.providerLabel || 'Provider'}</Label>
                    <Input data-testid={`${recordType}-provider`} value={form.provider} onChange={e => setForm(p => ({...p, provider: e.target.value}))} className="mt-1 h-8 text-sm font-ibm" />
                  </div>
                )}
                {hasField('issue_date') && (
                  <div>
                    <Label className="text-xs font-ibm text-text-dark">Issue Date</Label>
                    <Input data-testid={`${recordType}-issue-date`} type="date" value={form.issue_date} onChange={e => setForm(p => ({...p, issue_date: e.target.value}))} className="mt-1 h-8 text-sm font-ibm" />
                  </div>
                )}
                {hasField('expiry_date') && (
                  <div>
                    <Label className="text-xs font-ibm text-text-dark">{cfg.expiryLabel || 'Expiry Date'}</Label>
                    <Input data-testid={`${recordType}-expiry-date`} type="date" value={form.expiry_date} onChange={e => setForm(p => ({...p, expiry_date: e.target.value}))} className="mt-1 h-8 text-sm font-ibm" />
                  </div>
                )}
                {hasField('amount') && (
                  <div>
                    <Label className="text-xs font-ibm text-text-dark">Amount (₹)</Label>
                    <Input data-testid={`${recordType}-amount`} type="number" value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} className="mt-1 h-8 text-sm font-ibm" />
                  </div>
                )}
                {hasField('details') && (
                  <div>
                    <Label className="text-xs font-ibm text-text-dark">Details</Label>
                    <Input data-testid={`${recordType}-details`} value={form.details} onChange={e => setForm(p => ({...p, details: e.target.value}))} className="mt-1 h-8 text-sm font-ibm" />
                  </div>
                )}
                {hasField('remarks') && (
                  <div className="col-span-2 md:col-span-3">
                    <Label className="text-xs font-ibm text-text-dark">Remarks</Label>
                    <Textarea data-testid={`${recordType}-remarks`} value={form.remarks} onChange={e => setForm(p => ({...p, remarks: e.target.value}))} className="mt-1 text-sm font-ibm min-h-[60px]" />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button data-testid={`save-${recordType}-record`} onClick={handleSave} disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90 text-white h-8 px-4 text-sm font-ibm">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}{editRecord ? 'Update' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setEditRecord(null); setForm(emptyForm()); }} className="h-8 px-4 text-sm font-ibm">Cancel</Button>
              </div>
            </div>
          )}

          {/* Add button */}
          {!showForm && (
            <div className="flex items-center gap-2">
              <Button
                data-testid={`add-${recordType}-btn`}
                onClick={() => { setShowForm(true); setEditRecord(null); setForm(emptyForm()); }}
                variant="outline"
                className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white h-8 px-4 text-sm font-ibm"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />Add {cfg.label}
              </Button>
            </div>
          )}

          {/* Records table */}
          {records.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    {cfg.refLabel && <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">{cfg.refLabel}</th>}
                    {hasField('provider') && <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">{cfg.providerLabel || 'Provider'}</th>}
                    {hasField('issue_date') && <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">Issue Date</th>}
                    {hasField('expiry_date') && <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">{cfg.expiryLabel || 'Expiry Date'}</th>}
                    {hasField('amount') && <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">Amount</th>}
                    <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">Doc</th>
                    <th className="px-3 py-2 text-left text-xs text-text-secondary font-ibm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map(rec => (
                    <tr key={rec.id} className="hover:bg-gray-50">
                      {cfg.refLabel && <td className="px-3 py-2 font-ibm text-text-dark">{rec.reference_no || '—'}</td>}
                      {hasField('provider') && <td className="px-3 py-2 font-ibm text-text-secondary">{rec.provider || '—'}</td>}
                      {hasField('issue_date') && <td className="px-3 py-2 font-ibm text-text-secondary">{rec.issue_date || '—'}</td>}
                      {hasField('expiry_date') && (
                        <td className="px-3 py-2 font-ibm">
                          <span className={rec.expiry_date && new Date(rec.expiry_date) < new Date() ? 'text-accent-red font-semibold' : 'text-text-secondary'}>
                            {rec.expiry_date || '—'}
                          </span>
                        </td>
                      )}
                      {hasField('amount') && <td className="px-3 py-2 font-ibm text-text-secondary">{rec.amount ? `₹${rec.amount}` : '—'}</td>}
                      <td className="px-3 py-2">
                        {rec.drive_file_id ? (
                          <button onClick={() => handleDownload(rec)} className="text-accent-blue hover:underline text-xs font-ibm flex items-center gap-1">
                            <Download className="w-3 h-3" />{rec.file_name?.split('/').pop() || 'File'}
                          </button>
                        ) : driveConnected ? (
                          <label className="cursor-pointer text-xs text-text-secondary hover:text-accent-blue font-ibm flex items-center gap-1">
                            {uploadingId === rec.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            Upload
                            <input type="file" className="hidden" onChange={e => handleUpload(rec.id, e.target.files[0])} />
                          </label>
                        ) : <span className="text-xs text-gray-400 font-ibm">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => handleEdit(rec)} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded">
                            <Edit2 className="w-3.5 h-3.5 text-accent-blue" />
                          </button>
                          <button onClick={() => handleDelete(rec.id)} className="w-7 h-7 flex items-center justify-center hover:bg-accent-red/10 rounded">
                            <Trash2 className="w-3.5 h-3.5 text-accent-red" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {records.length === 0 && !showForm && (
            <p className="text-xs text-text-secondary font-ibm italic">No records yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VehicleRecordsTab — tab container; Attestr fetch lives here (top of docs tab)
// ─────────────────────────────────────────────────────────────────────────────
const VehicleRecordsTab = ({ vehicleId, category, backendUrl, getAuthHeaders, driveConnected, registrationNumber, onRcDataFetched }) => {
  const { toast } = useToast();
  const types = category === 'documents' ? DOCUMENT_TYPES : MAINTENANCE_TYPES;

  // Attestr fetch state
  const [showFetchPanel, setShowFetchPanel] = useState(false);
  const [fetchReg, setFetchReg] = useState('');
  const [fetching, setFetching] = useState(false);
  const [rcData, setRcData] = useState(null);
  // prefills keyed by record type, passed down to RecordSection
  const [prefills, setPrefills] = useState({});

  const openFetchPanel = () => {
    setFetchReg(registrationNumber || '');
    setRcData(null);
    setShowFetchPanel(true);
  };

  const handleFetchRC = async () => {
    if (!fetchReg.trim()) {
      toast({ title: 'Error', description: 'Enter a registration number', variant: 'destructive' });
      return;
    }
    setFetching(true);
    setRcData(null);
    try {
      const res = await fetch(`${backendUrl}/api/attestr/rc-check`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reg: fetchReg.trim().toUpperCase() }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error('Unexpected server response. Please try again shortly.');
      }
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch RC data');
      setRcData(data);
      toast({ title: 'RC Data Fetched', description: `Owner: ${data.owner || 'N/A'}` });
    } catch (e) {
      toast({ title: 'Fetch Failed', description: e.message, variant: 'destructive' });
    } finally {
      setFetching(false);
    }
  };

  const handleApplyRcData = () => {
    if (!rcData) return;
    const reg = fetchReg.trim().toUpperCase();

    const newPrefills = {};

    // RC Book — RC number, RTO/state, registration date
    newPrefills.RC_BOOK = {
      reference_no: reg,
      provider: rcData.rto || rcData.state || rcData.rtoCd || '',
      issue_date: parseAttestrDate(rcData.registered || ''),
      expiry_date: '',
    };

    // Fitness Certificate — expiry date
    const fitnessDate = validAttestrDate(rcData.fitnessUpto || rcData.fitness || '');
    if (fitnessDate) {
      newPrefills.FITNESS = { expiry_date: fitnessDate };
    }

    // Insurance — policy number, insurer, expiry
    const insuranceDate = validAttestrDate(rcData.insuranceUpto || rcData.insurance || '');
    const insPolicy = rcData.insurancePolicyNumber || '';
    const insProvider = rcData.insuranceProvider || '';
    if (insPolicy || insProvider || insuranceDate) {
      newPrefills.INSURANCE = {
        reference_no: insPolicy,
        provider: insProvider,
        expiry_date: insuranceDate,
      };
    }

    // PUC Certificate — certificate number, expiry
    const pucDate = validAttestrDate(rcData.pollutionCertificateUpto || '');
    const pucNo = rcData.pollutionCertificateNumber || '';
    if (pucNo || pucDate) {
      newPrefills.PUC = {
        reference_no: pucNo,
        expiry_date: pucDate,
      };
    }

    // Permit Record — permit number, type, dates
    const permitExpiry = validAttestrDate(rcData.permitUpto || '');
    const permitIssued = validAttestrDate(rcData.permitIssued || rcData.permitFrom || '');
    const permitNo = rcData.permitNumber || '';
    const permitType = rcData.permitType || '';
    if (permitNo || permitType || permitExpiry) {
      newPrefills.PERMIT = {
        reference_no: permitNo,
        provider: permitType,
        issue_date: permitIssued,
        expiry_date: permitExpiry,
      };
    }

    setPrefills(newPrefills);

    // Notify parent (VendorVehicleForm) to update engine_no + chasis_no
    if (onRcDataFetched) {
      onRcDataFetched({
        engine_no: rcData.engineNumber || '',
        chasis_no: rcData.chassisNumber || '',
      });
    }

    setShowFetchPanel(false);
    setRcData(null);

    const filledSections = Object.keys(newPrefills).join(', ');
    toast({
      title: 'RC Data Applied',
      description: `Auto-filled sections: ${filledSections}. Review and save each record.`,
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-text-secondary font-ibm">Click a section to expand and manage records. Expired dates are highlighted in red.</p>

      {/* ── Attestr Fetch Panel (Documents tab only) ── */}
      {category === 'documents' && (
        <div className="mb-1">
          {!showFetchPanel ? (
            <Button
              data-testid="fetch-rc-btn"
              onClick={openFetchPanel}
              variant="outline"
              className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white h-8 px-4 text-sm font-ibm"
            >
              <Search className="w-3.5 h-3.5 mr-1.5" />Fetch RC from Attestr
            </Button>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-green-800 font-ibm">Fetch RC from Attestr</h4>
                <button
                  onClick={() => { setShowFetchPanel(false); setRcData(null); }}
                  className="text-gray-400 hover:text-gray-600 text-xs font-ibm"
                >
                  ✕ Cancel
                </button>
              </div>

              {/* Limitation notice */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 font-ibm leading-relaxed">
                  <strong>Note:</strong> Attestr returns RC details, Fitness, Insurance, PUC, Permit metadata, Engine No. and Chassis No.
                  PDF document files are <strong>not</strong> available via this API.
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  data-testid="fetch-rc-reg-input"
                  value={fetchReg}
                  onChange={e => setFetchReg(e.target.value.toUpperCase())}
                  placeholder="e.g. GJ01AB1234"
                  className="h-9 text-sm border-green-300 font-ibm flex-1"
                />
                <Button
                  data-testid="fetch-rc-submit-btn"
                  onClick={handleFetchRC}
                  disabled={fetching}
                  className="bg-green-600 hover:bg-green-700 text-white h-9 px-4 text-sm font-ibm"
                >
                  {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  {fetching ? 'Fetching...' : 'Fetch'}
                </Button>
              </div>

              {rcData && (
                <div className="bg-white border border-green-200 rounded-md p-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700 font-ibm">RC Verified</span>
                  </div>

                  {/* RC data grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-ibm">
                    {[
                      ['Registration No.', fetchReg],
                      ['Owner', rcData.owner],
                      ['Status', rcData.status],
                      ['Registration Date', rcData.registered],
                      ['RTO / State', rcData.rto || rcData.state || rcData.rtoCd],
                      ['Vehicle Class', rcData.categoryDescription || rcData.vehicleClass],
                      ['Fuel Type', rcData.fuelType],
                      ['Make / Model', [rcData.makerDescription, rcData.makerModel].filter(Boolean).join(' ')],
                      ['Chassis No.', rcData.chassisNumber],
                      ['Engine No.', rcData.engineNumber],
                      ['Fitness Upto', validAttestrDate(rcData.fitnessUpto || rcData.fitness || '') || null],
                      ['Insurance Policy', rcData.insurancePolicyNumber],
                      ['Insurance Provider', rcData.insuranceProvider],
                      ['Insurance Upto', validAttestrDate(rcData.insuranceUpto || rcData.insurance || '') || null],
                      ['Pollution Cert No.', rcData.pollutionCertificateNumber],
                      ['Pollution Cert Upto', validAttestrDate(rcData.pollutionCertificateUpto || '') || null],
                      ['Permit No.', rcData.permitNumber],
                      ['Permit Type', rcData.permitType],
                      ['Permit Upto', validAttestrDate(rcData.permitUpto || '') || null],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label} className="bg-gray-50 rounded p-2">
                        <div className="text-gray-500 text-[10px]">{label}</div>
                        <div className="font-medium text-gray-900 break-all text-xs">{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* What will be auto-filled summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 mt-1">
                    <p className="text-xs text-blue-700 font-ibm font-semibold mb-1">Fields that will be auto-filled:</p>
                    <ul className="text-xs text-blue-600 font-ibm space-y-0.5 list-disc list-inside">
                      <li>RC Book — RC Number, RTO/State, Registration Date</li>
                      {validAttestrDate(rcData.fitnessUpto || rcData.fitness || '') && <li>Fitness Certificate — Fitness Upto date</li>}
                      {(rcData.insurancePolicyNumber || rcData.insuranceProvider || validAttestrDate(rcData.insuranceUpto || '')) && <li>Insurance — Policy No., Insurer, Upto date</li>}
                      {(rcData.pollutionCertificateNumber || validAttestrDate(rcData.pollutionCertificateUpto || '')) && <li>PUC Certificate — Certificate No., Upto date</li>}
                      {(rcData.permitNumber || rcData.permitType || validAttestrDate(rcData.permitUpto || '')) && <li>Permit Record — Permit No., Type, Dates</li>}
                      {(rcData.engineNumber || rcData.chassisNumber) && <li>Vehicle Basic Details — Engine No. &amp; Chassis No.</li>}
                    </ul>
                    <p className="text-xs text-amber-600 font-ibm mt-1.5 italic">
                      PDF documents are not available from Attestr. Fields with "1800-01-01" dates indicate no data from source.
                    </p>
                  </div>

                  <Button
                    data-testid="use-rc-data-btn"
                    onClick={handleApplyRcData}
                    className="bg-green-600 hover:bg-green-700 text-white h-8 px-4 text-sm font-ibm w-full mt-2"
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Apply RC Data &amp; Fill Forms
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Record Sections ── */}
      {types.map(type => (
        <RecordSection
          key={type}
          vehicleId={vehicleId}
          recordType={type}
          driveConnected={driveConnected}
          backendUrl={backendUrl}
          getAuthHeaders={getAuthHeaders}
          prefillData={prefills[type] || null}
        />
      ))}
    </div>
  );
};

export default VehicleRecordsTab;
